import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const QuizBadgesContext = createContext({ badges: new Set(), markPerfect: async () => false, practiceClears: 0, recordPracticeClear: async () => 0 });

export function QuizBadgesProvider({ children }) {
  const { user } = useAuth();
  const uid = user ? user.id || user._id || user.email : null;
  const [paths, setPaths] = useState([]);
  const [practiceClears, setPracticeClears] = useState(0);

  useEffect(() => {
    if (!uid) {
      setPaths([]);
      setPracticeClears(0);
      return;
    }
    axios
      .get("/api/quizzes/badges", { withCredentials: true })
      .then((r) => {
        setPaths(r.data.paths || []);
        setPracticeClears(r.data.practice_clears || 0);
      })
      .catch(() => {});
  }, [uid]);

  const markPerfect = useCallback(
    async (path) => {
      if (!uid || paths.includes(path)) return false;
      setPaths((p) => [...p, path]);
      try {
        await axios.post("/api/quizzes/perfect", { path }, { withCredentials: true });
        return true;
      } catch {
        setPaths((p) => p.filter((x) => x !== path));
        return false;
      }
    },
    [uid, paths]
  );

  const recordPracticeClear = useCallback(async () => {
    if (!uid) return 0;
    try {
      const { data } = await axios.post("/api/practice/cleared", {}, { withCredentials: true });
      setPracticeClears(data.practice_clears);
      return data.counted ? data.practice_clears : 0;
    } catch {
      return 0;
    }
  }, [uid]);

  const value = useMemo(
    () => ({ badges: new Set(paths), markPerfect, practiceClears, recordPracticeClear }),
    [paths, markPerfect, practiceClears, recordPracticeClear]
  );
  return <QuizBadgesContext.Provider value={value}>{children}</QuizBadgesContext.Provider>;
}

export const useQuizBadges = () => useContext(QuizBadgesContext);
